function hasAmount(amount) {
  return amount != null && String(amount).trim() !== "";
}

/** Baker-facing percent, e.g. 0.1475 → "+14.75%", 0.14 → "+14%". */
export function formatVariantOptionFactor(factor) {
  const percent = (Number(factor) * 100).toFixed(2).replace(/\.?0+$/, "");
  return `+${percent}%`;
}

export function resolveVariantOptionOverrides(option, overrides) {
  let allowed = true;
  let pricing = option?.pricing;
  let hasOverride = false;

  for (const [key, value] of Object.entries(overrides || {})) {
    const override = option?.override?.[key]?.[value];
    if (!override) continue;

    hasOverride = true;
    if (String(override.allow) === "false") {
      allowed = false;
      continue;
    }
    if (hasAmount(override.amount)) {
      pricing = { type: "fixed", amount: override.amount };
    }
  }

  return {
    allowed,
    hasOverride,
    option: pricing === option?.pricing ? option : { ...option, pricing },
    pricing,
  };
}

export function resolveVariantOptionForSize(option, sizeName) {
  return resolveVariantOptionOverrides(option, sizeName ? { size: sizeName } : {});
}

export function isPercentageSizeCell(option, sizeName) {
  const { allowed, pricing } = resolveVariantOptionForSize(option, sizeName);
  return allowed && pricing?.factor != null && pricing.factor !== "" && !hasAmount(pricing.amount);
}

export function applyVariantOptionSizeOverride(option, sizeName, { allowed, amount }) {
  const nextSize = { ...(option?.override?.size || {}) };

  if (allowed && !hasAmount(amount)) {
    delete nextSize[sizeName];
  } else if (!allowed) {
    nextSize[sizeName] = { allow: false };
  } else {
    nextSize[sizeName] = { allow: true, amount: String(amount).trim() };
  }

  const nextOverride = { ...(option?.override || {}) };
  if (Object.keys(nextSize).length > 0) {
    nextOverride.size = nextSize;
  } else {
    delete nextOverride.size;
  }

  const next = { ...option };
  if (Object.keys(nextOverride).length > 0) {
    next.override = nextOverride;
  } else {
    delete next.override;
  }
  return next;
}

/** Drop this size key so the option's default availability and pricing apply again. */
export function removeVariantOptionSizeOverride(option, sizeName) {
  return applyVariantOptionSizeOverride(option, sizeName, { allowed: true, amount: "" });
}

/** Standard (non-override) pricing is unchanged: same type, amount and factor. */
export function isSameVariantOptionPricing(a, b) {
  // Compare numerically so "0.14" and "0.1400" count as the same factor.
  const num = (v) => (v != null && String(v).trim() !== "" ? String(+v) : "");
  const norm = (pricing) => ({
    type: pricing?.type ?? "",
    amount: hasAmount(pricing?.amount) ? num(pricing.amount) : "",
    factor: num(pricing?.factor),
  });
  const p1 = norm(a);
  const p2 = norm(b);
  return p1.type === p2.type && p1.amount === p2.amount && p1.factor === p2.factor;
}

export function clearVariantOptionSizeOverrides(option) {
  if (!option?.override?.size) return option;

  const next = { ...option };
  const nextOverride = { ...option.override };
  delete nextOverride.size;

  if (Object.keys(nextOverride).length > 0) {
    next.override = nextOverride;
  } else {
    delete next.override;
  }
  return next;
}

export function hasVariantOptionSizeOverrides(variants) {
  return Object.values(variants || {}).some((variant) =>
    (variant?.field?.options || []).some((option) => getVariantOptionSizeOverrideCount(option) > 0)
  );
}

export function getVariantOptionSizeOverrideCount(option) {
  return Object.keys(option?.override?.size || {}).length;
}
